import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import { normalizeInlineGfmTables, maskIncompleteGfmTables, stabilizeMarkdownForStreaming } from '../utils/markdown';

const ReasoningSection = ({ section, theme, onToggle }) =>
  // Hide minimized view entirely once reasoning is done
  !section.open && section.done ? null : (
    <Box sx={{ mb: 2 }}>
      <Card sx={{ backgroundColor: theme.palette.grey[100] }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Reasoning
            </Typography>
            <Chip size="small" label={section.open ? 'Hide' : 'Show'} onClick={() => onToggle(section.id)} />
          </Box>
          {!section.open && (
            <Box sx={{ mt: 1 }}>
              {Array.isArray(section.steps) && section.steps.length > 0 ? (
                (() => {
                  const i = section.steps.length - 1;
                  const s = section.steps[i];
                  return (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        ...(section.done
                          ? {}
                          : {
                              animation: 'pulse 1.2s ease-in-out infinite',
                              '@keyframes pulse': { '0%': { opacity: 0.6 }, '50%': { opacity: 1 }, '100%': { opacity: 0.6 } }
                            })
                      }}
                    >
                      {`0.${i + 1} ${s.title || `Step ${i + 1}`}`}
                    </Typography>
                  );
                })()
              ) : Array.isArray(section.titles) && section.titles.length > 0 ? (
                (() => {
                  const i = section.titles.length - 1;
                  const t = section.titles[i];
                  return (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        ...(section.done
                          ? {}
                          : {
                              animation: 'pulse 1.2s ease-in-out infinite',
                              '@keyframes pulse': { '0%': { opacity: 0.6 }, '50%': { opacity: 1 }, '100%': { opacity: 0.6 } }
                            })
                      }}
                    >
                      {`0.${i + 1} ${t}`}
                    </Typography>
                  );
                })()
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    animation: 'pulse 1.2s ease-in-out infinite',
                    '@keyframes pulse': { '0%': { opacity: 0.6 }, '50%': { opacity: 1 }, '100%': { opacity: 0.6 } }
                  }}
                >
                  {section.titles && section.titles.length > 0
                    ? `0.${section.titles.length} ${section.titles[section.titles.length - 1]}`
                    : 'Thinking…'}
                </Typography>
              )}
            </Box>
          )}
          {section.open && (
            <Box sx={{ mt: 1 }}>
              {Array.isArray(section.steps) && section.steps.length > 0 ? (
                <Box>
                  {section.steps.map((step, idx) => (
                    <Box key={idx} sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {step.title || `Step ${idx + 1}`}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <MarkdownErrorBoundary content={step.content || ''}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{step.content || ''}</ReactMarkdown>
                        </MarkdownErrorBoundary>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <MarkdownErrorBoundary content={section.raw || ''}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {section.done
                        ? normalizeInlineGfmTables(section.raw || '')
                        : maskIncompleteGfmTables(stabilizeMarkdownForStreaming(normalizeInlineGfmTables(section.raw || '')))}
                    </ReactMarkdown>
                  </MarkdownErrorBoundary>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

export default ReasoningSection;
